import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, apiList, saveBlob } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import {
  CERT_TYPES,
  type CertType,
  type CertificateRow,
  type Message,
  type Person,
  type RequestRow,
} from './shared';

type Tab = 'register' | 'requests' | 'issue';

/** Staff side of certificates: the register, the families' requests, and issuing a new one. */
export function CertificatesPage() {
  const { can } = useAuth();
  const [schoolId, setSchoolId] = useState('');
  const [tab, setTab] = useState<Tab>('register');
  const tabs: Tab[] = [];
  if (schoolId && can('certificates.view', schoolId)) tabs.push('register', 'requests');
  if (schoolId && can('certificates.issue', schoolId)) tabs.push('issue');
  const active = tabs.includes(tab) ? tab : tabs[0];

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('cert.title')}</h1>
      <SchoolSelect value={schoolId} onChange={setSchoolId} />
      {schoolId && active && (
        <>
          <div
            role="tablist"
            aria-label={t('cert.title')}
            className="flex flex-wrap gap-1 border-b border-slate-200"
          >
            {tabs.map((name) => (
              <button
                key={name}
                role="tab"
                type="button"
                aria-selected={active === name}
                onClick={() => setTab(name)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${active === name ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-600'}`}
              >
                {t(`cert.tab.${name}` as MessageKey)}
              </button>
            ))}
          </div>
          <div role="tabpanel">
            {active === 'register' && <RegisterTab schoolId={schoolId} />}
            {active === 'requests' && <RequestsTab schoolId={schoolId} />}
            {active === 'issue' && <IssueTab schoolId={schoolId} />}
          </div>
        </>
      )}
    </div>
  );
}

function RegisterTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const list = useQuery({
    queryKey: ['certificates', schoolId, 'register', q, page],
    queryFn: () =>
      apiList<CertificateRow>(`/schools/${schoolId}/certificates`, {
        query: { q: q.trim() || undefined, page, pageSize: 20 },
      }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/certificates/${id}/revoke`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      setRevoking(null);
      setReason('');
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['certificates', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const pdf = async (c: CertificateRow) => {
    try {
      saveBlob(
        await apiBlob(`/schools/${schoolId}/certificates/${c.id}/certificate.pdf`),
        `${c.certificateNumber}.pdf`,
      );
    } catch (e) {
      setMessage({ kind: 'error', text: errorMessage(e) });
    }
  };

  return (
    <section aria-label={t('cert.tab.register')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <TextField
        label={t('cert.search')}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setPage(1);
        }}
      />
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('cert.empty')}</p>}
      <ul className="space-y-2">
        {list.data?.data.map((c) => (
          <li key={c.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <strong>{c.certificateNumber}</strong> · {t(`cert.type.${c.type}` as MessageKey)} ·{' '}
                {c.studentName} · {c.issueDate}
              </span>
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${c.status === 'revoked' ? 'bg-red-50 text-red-800' : 'bg-slate-100'}`}
              >
                {t(`cert.status.${c.status}` as MessageKey)}
              </span>
            </div>
            <p className="mt-1 text-slate-600">
              {t('cert.code')}: {c.verificationCode}
              {c.revokeReason ? ` · ${c.revokeReason}` : ''}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('cert.pdf')} ${c.certificateNumber}`}
                onClick={() => void pdf(c)}
              >
                {t('cert.pdf')}
              </button>
              {c.status === 'issued' && can('certificates.revoke', schoolId) && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('cert.revoke')} ${c.certificateNumber}`}
                  onClick={() => setRevoking(c.id)}
                >
                  {t('cert.revoke')}
                </button>
              )}
            </div>
            {revoking === c.id && (
              <form
                className="mt-2 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  revoke.mutate(c.id);
                }}
              >
                <TextField
                  label={t('cert.revokeReason')}
                  value={reason}
                  maxLength={300}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button type="submit" className={primaryButton} disabled={!reason.trim()}>
                  {t('cert.confirmRevoke')}
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {list.data && list.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
      )}
    </section>
  );
}

function RequestsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);
  const [declining, setDeclining] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const list = useQuery({
    queryKey: ['certificates', schoolId, 'requests', page],
    queryFn: () =>
      apiList<RequestRow>(`/schools/${schoolId}/certificate-requests`, {
        query: { page, pageSize: 20 },
      }),
  });
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'approve' | 'reject'; note?: string }) =>
      api(`/schools/${schoolId}/certificate-requests/${v.id}/decide`, {
        method: 'POST',
        body: { decision: v.decision, note: v.note },
      }),
    onSuccess: () => {
      setDeclining(null);
      setNote('');
      setMessage({ kind: 'ok', text: t('cert.decided') });
      void queryClient.invalidateQueries({ queryKey: ['certificates', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  return (
    <section aria-label={t('cert.tab.requests')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('cert.req.empty')}</p>}
      <ul className="space-y-2">
        {list.data?.data.map((r) => (
          <li key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <strong>{r.studentName}</strong> · {t(`cert.type.${r.type}` as MessageKey)} —{' '}
                {r.purpose}
              </span>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                {t(`cert.req.status.${r.status}` as MessageKey)}
              </span>
            </div>
            {r.decisionNote && <p className="mt-1 text-slate-600">{r.decisionNote}</p>}
            {r.status === 'pending' && can('certificates.decide', schoolId) && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={primaryButton}
                  aria-label={`${t('cert.approve')} ${r.studentName}`}
                  disabled={decide.isPending}
                  onClick={() => {
                    setMessage(null);
                    decide.mutate({ id: r.id, decision: 'approve' });
                  }}
                >
                  {t('cert.approve')}
                </button>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('cert.decline')} ${r.studentName}`}
                  onClick={() => setDeclining(r.id)}
                >
                  {t('cert.decline')}
                </button>
              </div>
            )}
            {declining === r.id && (
              <form
                className="mt-2 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  decide.mutate({ id: r.id, decision: 'reject', note });
                }}
              >
                <TextField
                  label={t('cert.declineReason')}
                  value={note}
                  maxLength={300}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button type="submit" className={primaryButton} disabled={!note.trim()}>
                  {t('cert.confirmDecline')}
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {list.data && list.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
      )}
    </section>
  );
}

function IssueTab({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [type, setType] = useState<CertType>('bonafide');
  const [form, setForm] = useState({
    purpose: '',
    printedName: '',
    leavingDate: '',
    reason: '',
    conduct: '',
  });
  const [message, setMessage] = useState<Message>(null);

  const matches = useQuery({
    queryKey: ['certificates', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: search.trim().length >= 2 && !person,
  });
  const issue = useMutation({
    mutationFn: () =>
      api<CertificateRow>(`/schools/${schoolId}/certificates`, {
        method: 'POST',
        body: {
          studentId: person!.id,
          type,
          purpose: form.purpose.trim() || undefined,
          printedName: form.printedName.trim() || undefined,
          conduct: form.conduct.trim() || undefined,
          ...(type === 'transfer'
            ? {
                leavingDate: form.leavingDate || undefined,
                reason: form.reason.trim() || undefined,
              }
            : {}),
        },
      }),
    onSuccess: (c) => {
      setMessage({ kind: 'ok', text: t('cert.issued', { number: c.certificateNumber }) });
      setForm({ purpose: '', printedName: '', leavingDate: '', reason: '', conduct: '' });
      void queryClient.invalidateQueries({ queryKey: ['certificates', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const set = (field: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [field]: e.target.value });

  return (
    <section aria-label={t('cert.tab.issue')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!person) return;
          setMessage(null);
          issue.mutate();
        }}
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
        aria-label={t('cert.issue')}
      >
        <div className="relative">
          <TextField
            label={t('cert.student')}
            value={person ? `${person.firstName} ${person.lastName}` : search}
            onChange={(e) => {
              setPerson(null);
              setSearch(e.target.value);
            }}
          />
          {!person && matches.data && matches.data.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 bg-white text-sm shadow">
              {matches.data.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => setPerson(p)}
                  >
                    {p.firstName} {p.lastName}
                    {p.admissionNumber ? ` (${p.admissionNumber})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label htmlFor="cert-type" className="block text-sm font-medium">
            {t('cert.type')}
          </label>
          <select
            id="cert-type"
            className={selectClass}
            value={type}
            onChange={(e) => setType(e.target.value as CertType)}
          >
            {CERT_TYPES.map((c) => (
              <option key={c} value={c}>
                {t(`cert.type.${c}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        <TextField
          label={t('cert.purpose')}
          value={form.purpose}
          maxLength={300}
          onChange={set('purpose')}
        />
        <TextField
          label={t('cert.conduct')}
          value={form.conduct}
          maxLength={100}
          onChange={set('conduct')}
        />
        {type === 'transfer' && (
          <>
            <TextField
              label={t('cert.leavingDate')}
              type="date"
              value={form.leavingDate}
              onChange={set('leavingDate')}
            />
            <TextField
              label={t('cert.reason')}
              value={form.reason}
              maxLength={300}
              onChange={set('reason')}
            />
          </>
        )}
        <TextField
          label={t('cert.printedName')}
          value={form.printedName}
          maxLength={200}
          onChange={set('printedName')}
        />
        <div className="sm:col-span-2">
          <button type="submit" className={primaryButton} disabled={!person || issue.isPending}>
            {t('cert.issue')}
          </button>
        </div>
      </form>
    </section>
  );
}
